/**
 * useCalendarEvents — reads v9_calendar_events_cache for the current tenant
 * within a [start, end] window, with optional source filtering.
 *
 * Tiny custom hook (no React Query in this project) — re-runs when the tenant
 * or window changes. Manual `refresh()` triggers the server sync then re-reads.
 */
import { useCallback, useEffect, useState } from 'react';
import type { CalendarEvent, CalendarSource } from '../lib/calendar';
import { listCalendarEvents, triggerCalSync } from '../lib/calendar';
import { useTenant } from '../contexts/TenantContext';

interface UseCalendarEventsOptions {
  startIso: string;
  endIso: string;
  sources?: CalendarSource[];   // default: all three
}

export function useCalendarEvents({ startIso, endIso, sources }: UseCalendarEventsOptions) {
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? null;

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listCalendarEvents({ tenantId, startIso, endIso });
      const filtered = sources ? data.filter((e) => sources.includes(e.source)) : data;
      setEvents(filtered);
    } catch (err) {
      setError((err as Error).message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, startIso, endIso, sources]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    try {
      await triggerCalSync();
    } catch (err) {
      // surface non-fatal — we still re-read the cache.
      console.warn('[useCalendarEvents] sync failed:', (err as Error).message);
    }
    await load();
  }, [load]);

  return { events, loading, error, refresh };
}
